import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { CTASection } from "@/components/home/CTASection";
import { Reveal } from "@/components/common/Reveal";
import { ArrowRight, PlayCircle, MapPin } from "@phosphor-icons/react";

function CaseStudyCard({ cs, index }) {
  const { t } = useT();
  return (
    <Reveal delay={(index % 3) * 0.08}>
      <Link
        to={`/case-studies/${cs.slug}`}
        data-testid={`case-study-card-${cs.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-teal/50 hover:shadow-xl"
      >
        <div className="h-52 overflow-hidden">
          <img src={cs.cover_image} alt={cs.title} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
        <div className="flex flex-1 flex-col p-6">
          <div className="flex items-center gap-2 text-[11px] font-semibold">
            <span className="rounded bg-teal/10 px-2 py-0.5 text-teal">{cs.industry}</span>
            <span className="flex items-center gap-1 text-slate-400"><MapPin size={12} /> {cs.country}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-ink">{cs.title}</h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500 line-clamp-3">{cs.summary}</p>
          <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
            <div className="flex gap-4">
              {(cs.metrics || []).slice(0, 2).map((m) => (
                <div key={m.label}>
                  <p className="font-heading text-lg font-bold text-ink">{m.value}</p>
                  <p className="text-[10px] text-slate-400">{m.label}</p>
                </div>
              ))}
            </div>
            <span className="flex items-center gap-1 text-sm font-semibold text-teal">
              {t("caseStudies.read")} <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

export default function CaseStudies() {
  const { t, lang } = useT();
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    api.get("/case-studies").then((r) => setStudies(r.data)).catch((e) => console.error("Failed to load case studies:", e));
  }, [lang]);

  return (
    <div data-testid="case-studies-page">
      <PageHero
        overline={t("caseStudies.overline")}
        title={t("caseStudies.title")}
        subtitle={t("caseStudies.subtitle")}
        crumbs={[{ label: t("caseStudies.overline") }]}
        image="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg"
      />
      <section className="bg-mist py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {studies.map((cs, i) => <CaseStudyCard key={cs.id} cs={cs} index={i} />)}
          </div>
          {studies.length === 0 && <p className="py-16 text-center text-slate-400">Loading…</p>}
          <div className="mt-12 text-center">
            <Link to="/videos" data-testid="case-studies-videos-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-teal hover:underline">
              <PlayCircle size={18} weight="fill" /> {t("caseStudies.watch")}
            </Link>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
